/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { defineMessages } from 'react-intl';

import {
  LAUNCHER_DESKTOP_TITLE_MAX_LENGTH,
  LAUNCHER_MOBILE_TITLE_MAX_LENGTH,
  STARTERS_MAX_LENGTH,
  WEB_CHAT_SIZE_MAX_LENGTH
} from '../../util/validation/constants';

// TODO: Add descriptions to all messages

export const Defaults = defineMessages({
  botName: {
    id: 'webchat.Defaults.botName',
    defaultMessage: 'Assistant',
    description: ''
  },
  contactLabel: {
    id: 'webchat.Defaults.contactLabel',
    defaultMessage: 'Connect to agent',
    description: ''
  },
  greeting: {
    id: 'webchat.Defaults.greeting',
    defaultMessage: 'Hi! I’m a virtual assistant. How can I help you today?',
    description: ''
  },
  starter0: {
    id: 'webchat.Defaults.starter0',
    defaultMessage: 'Example: Find nearby location',
    description: ''
  },
  starter1: {
    id: 'webchat.Defaults.starter1',
    defaultMessage: 'Example: Check account balance',
    description: ''
  },
  starter2: {
    id: 'webchat.Defaults.starter2',
    defaultMessage: 'Example: See how I can help',
    description: ''
  },
  starter3: {
    id: 'webchat.Defaults.starter3',
    defaultMessage: 'Example: Schedule an appointment',
    description: ''
  },
  starter4: {
    id: 'webchat.Defaults.starter4',
    defaultMessage: 'Example: Make a payment',
    description: ''
  }
});

export const EmbedTab = defineMessages({
  // TODO: this text is the same in security tab -- perhaps we can make a generic section and declare it only once?
  codeSnippetErrorText: {
    id: 'webchat.EmbedTab.codeSnippetErrorText',
    defaultMessage:
      'Please save any changes you want to keep and refresh this page to try again.',
    description: ''
  },
  codeSnippetErrorTitle: {
    id: 'webchat.EmbedTab.codeSnippetErrorTitle',
    defaultMessage: 'Unable to load embed snippet',
    description: ''
  },
  subtitle: {
    id: 'webchat.EmbedTab.subtitle',
    defaultMessage: "Ready to launch? It's as easy as copy and paste.",
    description: ''
  },
  title: {
    id: 'webchat.EmbedTab.title',
    defaultMessage: 'Embed on your website',
    description: ''
  }
});

export const LauncherTab = defineMessages({
  title: {
    id: 'webchat.LauncherTab.title',
    defaultMessage: 'Launcher',
    description: ''
  },
  subtitle: {
    id: 'webchat.LauncherTab.subtitle',
    defaultMessage: 'Show your customers that web chat is here to help.',
    description: ''
  },
  desktopLauncherTitle: {
    id: 'webchat.LauncherTab.desktopLauncherTitle',
    defaultMessage: 'Default desktop greeting message behavior',
    description: ''
  },
  desktopLauncherDescription: {
    id: 'webchat.LauncherTab.desktopLauncherDescription',
    defaultMessage:
      'Web chat will surface this greeting message 15 seconds after a user lands on your website. This message will appear once per session.',
    description: ''
  },
  mobileLauncherTitle: {
    id: 'webchat.LauncherTab.mobileLauncherTitle',
    defaultMessage: 'Default mobile greeting message behavior',
    description: ''
  },
  mobileLauncherDescription: {
    id: 'webchat.LauncherTab.mobileLauncherDescription',
    defaultMessage:
      'Web chat will surface this greeting message 10 seconds after a user lands on your website. It will close automatically after 10 seconds, or the user can also swipe or scroll to close. This message will appear once per session.',
    description: ''
  },
  desktopTabLabel: {
    id: 'webchat.LauncherTab.desktopTabLabel',
    defaultMessage: 'Edit desktop launcher',
    description: ''
  },
  mobileTabLabel: {
    id: 'webchat.LauncherTab.mobileTabLabel',
    defaultMessage: 'Edit mobile launcher',
    description: ''
  },
  desktopLauncherLabel: {
    id: 'webchat.LauncherTab.desktopLauncherLabel',
    defaultMessage: 'Greeting message on desktop and tablets',
    description: ''
  },
  mobileLauncherLabel: {
    id: 'webchat.LauncherTab.mobileLauncherLabel',
    defaultMessage: 'Greeting message on smart phones',
    description: ''
  },
  desktopLauncherPlaceholder: {
    id: 'webchat.LauncherTab.desktopLauncherPlaceholder',
    defaultMessage: `Hi! I'm a virtual assistant. How can I help you today?`,
    description: ''
  },
  mobileLauncherPlaceholder: {
    id: 'webchat.LauncherTab.mobileLauncherPlaceholder',
    defaultMessage: 'Hi! How can I help you today?',
    description: ''
  }
});

export const HomeScreenTab = defineMessages({
  enable: {
    id: 'webchat.HomeScreenTab.enable',
    defaultMessage: 'Enable home screen',
    description: ''
  },
  backgroundTitle: {
    id: 'webchat.HomeScreenTab.backgroundTitle',
    defaultMessage: 'Background style',
    description: ''
  },
  backgroundLabel: {
    id: 'webchat.HomeScreenTab.backgroundLabel',
    defaultMessage: "Choose a style for your home screen's background",
    description: ''
  },
  backgroundOptionNone: {
    id: 'webchat.HomeScreenTab.backgroundOptionNone',
    defaultMessage: 'None',
    description: ''
  },
  backgroundOptionSolid: {
    id: 'webchat.HomeScreenTab.backgroundOptionSolid',
    defaultMessage: 'Accent color',
    description: ''
  },
  backgroundOptionGradientTopCornerOut: {
    id: 'webchat.HomeScreenTab.backgroundOptionTopCornerOut',
    defaultMessage: 'Gradient from the top corner',
    description: ''
  },
  backgroundOptionGradientBottomUp: {
    id: 'webchat.HomeScreenTab.backgroundOptionGradientBottomUp',
    defaultMessage: 'Gradient from the bottom',
    description: ''
  },
  greetingDescription: {
    id: 'webchat.HomeScreenTab.greetingDescription',
    defaultMessage:
      'Good greetings are welcoming, actionable, and expressive of your assistant’s personality.',
    description: ''
  },
  greetingLabel: {
    id: 'webchat.HomeScreenTab.greetingLabel',
    defaultMessage: 'Greeting message',
    description: ''
  },
  greetingNote: {
    id: 'webchat.HomeScreenTab.greetingNote',
    defaultMessage:
      'This message will override the dialog welcome node and Actions greeting.',
    description: ''
  },
  greetingTitle: {
    id: 'webchat.HomeScreenTab.greetingTitle',
    defaultMessage: 'Greeting',
    description: ''
  },
  startersButtonText: {
    id: 'webchat.HomeScreenTab.startersButtonText',
    defaultMessage: 'Add a starter ',
    description: ''
  },
  startersDescription: {
    id: 'webchat.HomeScreenTab.startersDescription',
    defaultMessage:
      'Help your customers get going. When selected, the text of these starters is sent as a message. Each should be something your assistant is trained and tested to answer.',
    description: ''
  },
  starterLabel0: {
    id: 'webchat.HomeScreenTab.starterLabel0',
    defaultMessage: 'Conversation starter 1',
    description: ''
  },
  starterLabel1: {
    id: 'webchat.HomeScreenTab.starterLabel1',
    defaultMessage: 'Conversation starter 2',
    description: ''
  },
  starterLabel2: {
    id: 'webchat.HomeScreenTab.starterLabel2',
    defaultMessage: 'Conversation starter 3',
    description: ''
  },
  starterLabel: {
    id: 'webchat.HomeScreenTab.starterLabel',
    defaultMessage: 'Conversation starter {starterNum}'
  },
  startersTitle: {
    id: 'webchat.HomeScreenTab.startersTitle',
    defaultMessage: 'Conversation starters',
    description: ''
  },
  subtitle: {
    id: 'webchat.HomeScreenTab.subtitle',
    defaultMessage:
      'Make a good first impression and get customers the help they need more quickly.',
    description: ''
  },
  title: {
    id: 'webchat.HomeScreenTab.title',
    defaultMessage: 'Home screen',
    description: ''
  }
});

export const LiveAgentTab = defineMessages({
  changeLabel: {
    id: 'webchat.LiveAgentTab.changeLabel',
    defaultMessage: 'Change service desk',
    description: ''
  },
  changeWarningDescription: {
    id: 'webchat.LiveAgentTab.changeWarningDescription',
    defaultMessage:
      'Current service desk information will be lost if the service desk is changed.',
    description: ''
  },
  changeWarningTitle: {
    id: 'webchat.LiveAgentTab.changeWarningTitle',
    defaultMessage: 'Changing service desk',
    description: ''
  },
  connectLabel: {
    id: 'webchat.LiveAgentTab.connectLabel',
    defaultMessage: 'Connect',
    description: ''
  },
  createLabel: {
    id: 'webchat.LiveAgentTab.createLabel',
    defaultMessage: 'Create & add',
    description: ''
  },
  enable: {
    id: 'webchat.LiveAgentTab.enable',
    defaultMessage: 'Enable service desk',
    description: ''
  },
  installLabel: {
    id: 'webchat.LiveAgentTab.installLabel',
    defaultMessage: 'Install',
    description: ''
  },
  privateAppLabel: {
    id: 'webchat.LiveAgentTab.privateAppLabel',
    defaultMessage: 'Private app',
    description: ''
  },
  subtitle: {
    id: 'webchat.LiveAgentTab.subtitle',
    defaultMessage:
      'Set up a service desk connection so your assistant can transfer customers to live agents.',
    description: ''
  },
  title: {
    id: 'webchat.LiveAgentTab.title',
    defaultMessage: 'Live agent integrations',
    description: ''
  },
  transfersDisabled: {
    id: 'webchat.LiveAgentTab.transfersDisabled',
    defaultMessage: 'Transfers disabled',
    description: ''
  },
  transfersEnabled: {
    id: 'webchat.LiveAgentTab.transfersEnabled',
    defaultMessage: 'Transfers enabled',
    description: ''
  },
  transferSupportDescription: {
    id: 'webchat.LiveAgentTab.transferSupportDescription',
    defaultMessage:
      'Teach your assistant to recognize when a customer wants to speak to a person so it can transfer the conversation. <link>Learn more</link>',
    description: ''
  },
  transferSupportTitle: {
    id: 'webchat.LiveAgentTab.transferSupportTitle',
    defaultMessage: 'Add transfer support to your dialog',
    description: ''
  },
  visualforcePageLabel: {
    id: 'webchat.LiveAgentTab.visualforcePageLabel',
    defaultMessage: 'Visualforce page',
    description: ''
  }
});

export const SecurityTab = defineMessages({
  enable: {
    id: 'webchat.SecurityTab.enable',
    defaultMessage: 'Enable security',
    description: ''
  },
  IBMPublicKeyErrorText: {
    id: 'webchat.SecurityTab.IBMPublicKeyErrorText',
    defaultMessage:
      'Please save any changes you want to keep and refresh this page to try again.',
    description: ''
  },
  IBMPublicKeyErrorTitle: {
    id: 'webchat.SecurityTab.IBMPublicKeyErrorTitle',
    defaultMessage: 'Unable to load IBM provided public key',
    description: ''
  },
  IBMPublicKeyLabel: {
    id: 'webchat.SecurityTab.IBMPublicKeyLabel',
    defaultMessage: 'IBM provided public key',
    description: ''
  },
  IBMPublicKeyLoading: {
    id: 'webchat.SecurityTab.IBMPublicKeyLoading',
    defaultMessage: 'Loading public key',
    description: ''
  },
  IBMPublicKeyText: {
    id: 'webchat.SecurityTab.IBMPublicKeyText',
    defaultMessage:
      'You can use this public key to encrypt data that you do not want your users to see. For example, you might want to add to your context if the user is a VIP or not.',
    description: ''
  },
  IBMPublicKeyTitle: {
    id: 'webchat.SecurityTab.IBMPublicKeyTitle',
    defaultMessage: 'Encrypt sensitive information',
    description: ''
  },
  IBMPublicKeyEmptyButtonTitle: {
    id: 'webchat.SecurityTab.IBMPublicKeyEmptyButtonTitle',
    defaultMessage: 'Generate key',
    description: ''
  },
  publicKeyLabel: {
    id: 'webchat.SecurityTab.publicKeyLabel',
    defaultMessage: 'Your public key',
    description: ''
  },
  publicKeyText: {
    id: 'webchat.SecurityTab.publicKeyText',
    defaultMessage:
      'By providing IBM with a public key to verify RS256 signed messages, we can make sure that web chat traffic is coming from you and that any user information you provide is secure and accurate.',
    description: ''
  },
  publicKeyTitle: {
    id: 'webchat.SecurityTab.publicKeyTitle',
    defaultMessage: 'Verify web chat users are who they say they are',
    description: ''
  },
  subtitle: {
    id: 'webchat.SecurityTab.subtitle',
    defaultMessage:
      'Encrypt sensitive data, and authenticate and authorize your web chat and users.',
    description: ''
  },
  title: {
    id: 'webchat.SecurityTab.title',
    defaultMessage: 'Secure your web chat',
    description: ''
  }
});

export const StyleTab = defineMessages({
  accentHelperText: {
    id: 'webchat.StyleTab.accentHelperText',
    defaultMessage: 'Significant and interactive objects',
    description: ''
  },
  accentLabel: {
    id: 'webchat.StyleTab.accentLabel',
    defaultMessage: 'Accent color',
    description: ''
  },
  aiThemeLabel: {
    id: 'webchat.StyleTab.aiThemeLabel',
    defaultMessage: 'Intended purpose',
    description: ''
  },
  aiThemeOptionDisabled: {
    id: 'webchat.StyleTab.aiThemeOptionDisabled',
    defaultMessage:
      'Standard: For virtual agents and customer support experiences.',
    description: ''
  },
  aiThemeOptionEnabled: {
    id: 'webchat.StyleTab.aiThemeOptionEnabled',
    defaultMessage: 'Carbon for AI: For use in internal IBM products.',
    description: ''
  },
  avatarAdd: {
    id: 'webchat.StyleTab.avatarAdd',
    defaultMessage: 'Add an avatar image',
    description: ''
  },
  avatarChange: {
    id: 'webchat.StyleTab.avatarChange',
    defaultMessage: 'Change avatar image',
    description: ''
  },
  avatarDescription: {
    id: 'webchat.StyleTab.avatarDescription',
    defaultMessage:
      'Add the URL for an image to represent your assistant, such as a logo or avatar, in the chat header. URL must be public.',
    description: ''
  },
  avatarImageError: {
    id: 'webchat.StyleTab.avatarImageError',
    defaultMessage:
      'Image could not be found. Confirm your URL is publicly accessible and returns a valid image file.',
    description: ''
  },
  avatarImageSizeDescription: {
    id: 'webchat.StyleTab.avatarImageSizeDescription',
    defaultMessage:
      'Image must be square with a resolution between {minSize} x {minSize} and {maxSize} x {maxSize}.',
    description: ''
  },
  avatarImageSizeError: {
    id: 'webchat.StyleTab.avatarImageSizeError',
    defaultMessage:
      'Incorrect size. Image must be square with a resolution between {minSize} x {minSize} and {maxSize} x {maxSize}.',
    description: ''
  },
  avatarModalClose: {
    id: 'webchat.StyleTab.avatarModalClose',
    defaultMessage: 'Close',
    description: ''
  },
  avatarModalHeader: {
    id: 'webchat.StyleTab.avatarModalHeader',
    defaultMessage: 'Assistant avatar image',
    description: ''
  },
  avatarModalSave: {
    id: 'webchat.StyleTab.avatarModalSave',
    defaultMessage: 'Save',
    description: ''
  },
  avatarPlaceholder: {
    id: 'webchat.StyleTab.avatarPlaceholder',
    defaultMessage: 'Add image URL',
    description: ''
  },
  botNameLabel: {
    id: 'webchat.StyleTab.botNameLabel',
    defaultMessage: "Assistant's name as known by customers",
    description: ''
  },
  brandingLabel: {
    id: 'webchat.StyleTab.brandingLabel',
    defaultMessage: 'IBM Watermark',
    description: ''
  },
  brandingSubLabel: {
    id: 'webchat.StyleTab.brandingSubLabel',
    defaultMessage: 'Displays a link to the {productName} website',
    description: ''
  },
  carbonThemeLabel: {
    id: 'webchat.StyleTab.carbonThemeLabel',
    defaultMessage: 'Choose a theme',
    description: ''
  },
  carbonThemeOptionLight: {
    id: 'webchat.StyleTab.carbonThemeOptionLight',
    defaultMessage: 'Light',
    description: ''
  },
  carbonThemeOptionDark: {
    id: 'webchat.StyleTab.carbonThemeOptionDark',
    defaultMessage: 'Dark',
    description: ''
  },
  colorPickerLabel: {
    id: 'webchat.StyleTab.colorPickerLabel',
    defaultMessage: 'Open color picker',
    description: ''
  },
  enableBranding: {
    id: 'webchat.StyleTab.enableBranding',
    defaultMessage: 'Enable IBM Watermark',
    description: ''
  },
  enableStreaming: {
    id: 'webchat.StyleTab.enableStreaming',
    defaultMessage: 'Enable Streaming',
    description: ''
  },
  height: {
    id: 'webchat.StyleTab.height',
    defaultMessage: 'Height',
    description: ''
  },
  previewPageLinkText: {
    id: 'webchat.StyleTab.previewPageLinkText',
    defaultMessage: 'Preview page',
    description: ''
  },
  primaryHelperText: {
    id: 'webchat.StyleTab.primaryHelperText',
    defaultMessage: 'Chat header',
    description: ''
  },
  primaryLabel: {
    id: 'webchat.StyleTab.primaryLabel',
    defaultMessage: 'Primary color',
    description: ''
  },
  secondaryHelperText: {
    id: 'webchat.StyleTab.secondaryHelperText',
    defaultMessage: 'User message bubble',
    description: ''
  },
  secondaryLabel: {
    id: 'webchat.StyleTab.secondaryLabel',
    defaultMessage: 'Secondary color',
    description: ''
  },
  size: {
    id: 'webchat.StyleTab.size',
    defaultMessage: 'Size',
    description: ''
  },
  sizeNote: {
    id: 'webchat.StyleTab.sizeNote',
    defaultMessage:
      'The size of the web chat on this page will not change by updating these fields.',
    description: ''
  },
  streamingLabel: {
    id: 'webchat.StyleTab.aiThemeLabel',
    defaultMessage: 'Streaming',
    description: ''
  },
  subtitle: {
    id: 'webchat.StyleTab.subtitle',
    defaultMessage:
      'Update the style to match your brand and your website. A developer can also add more advanced styling changes with code.',
    description: ''
  },
  subtitleOnboarding: {
    id: 'webchat.StyleTab.subtitleOnboarding',
    defaultMessage:
      'Update the style to match your brand and your website. You can change these settings later. A developer can also add more advanced styling changes with code.',
    description: ''
  },
  title: {
    id: 'webchat.StyleTab.title',
    defaultMessage: 'Customize your chat UI',
    description: ''
  },
  visitPreviewPageText: {
    id: 'webchat.StyleTab.visitPreviewPageText',
    defaultMessage: 'To see your changes visit the',
    description: ''
  },
  width: {
    id: 'webchat.StyleTab.width',
    defaultMessage: 'Width',
    description: ''
  }
});

export const SuggestionsTab = defineMessages({
  connectCustomersDescription: {
    id: 'webchat.SuggestionsTab.connectCustomersDescription',
    defaultMessage:
      "Can be any alternative help, such as a live agent or your company's contact information when agents are unavailable.",
    description: ''
  },
  connectCustomersTitle: {
    id: 'webchat.SuggestionsTab.connectCustomersTitle',
    defaultMessage: 'Include a connection to support',
    description: ''
  },
  contactOptionAlways: {
    id: 'webchat.SuggestionsTab.contactOptionAlways',
    defaultMessage: 'Always',
    description: ''
  },
  contactOptionFailedAttempt: {
    id: 'webchat.SuggestionsTab.contactOptionFailedAttempt',
    defaultMessage: 'After one failed attempt',
    description: ''
  },
  contactOptionLabel: {
    id: 'webchat.SuggestionsTab.contactOptionLabel',
    defaultMessage: 'When should suggestions include connection to support?',
    description: ''
  },
  contactOptionNever: {
    id: 'webchat.SuggestionsTab.contactOptionNever',
    defaultMessage: 'Never',
    description: ''
  },
  enable: {
    id: 'webchat.SuggestionsTab.enable',
    defaultMessage: 'Enable suggestions',
    description: ''
  },
  failedAttemptTooltip: {
    id: 'webchat.SuggestionsTab.failedAttemptTooltip',
    defaultMessage: 'A failed attempt is when the bot cannot answer a question',
    description: ''
  },
  moreInformationLabel: {
    id: 'webchat.SuggestionsTab.moreInformationLabel',
    defaultMessage: 'More information',
    description: ''
  },
  optionLabelHelperText: {
    id: 'webchat.SuggestionsTab.optionLabelHelperText',
    defaultMessage:
      'When selected, this text is sent as a message to your assistant. Your assistant must be trained and tested to respond. Tip: Responses should be generically useful in many scenarios when your customer might need alternative support.',
    description: ''
  },
  optionLabel: {
    id: 'webchat.SuggestionsTab.optionLabel',
    defaultMessage: 'Option label',
    description: ''
  },
  optionLabelPlaceholder: {
    id: 'webchat.SuggestionsTab.optionLabelPlaceholder',
    defaultMessage: 'For example: Connect to agent',
    description: ''
  },
  subtitle: {
    id: 'webchat.SuggestionsTab.subtitle',
    defaultMessage:
      'Suggestions appear as message options when your customers need a bit of help. They adapt to the conversation and can offer both responses to try and connection to support.',
    description: ''
  },
  title: {
    id: 'webchat.SuggestionsTab.title',
    defaultMessage: 'Suggestions',
    description: ''
  }
});

export const ResourcesTab = defineMessages({
  title: {
    id: 'webchat.resourcesTab.title',
    defaultMessage: 'Explore your resources',
    description: 'Title of resources tab page'
  },
  demoSiteTag: {
    id: 'webchat.resourcesTab.demoSiteTileTag',
    defaultMessage: 'Demo site',
    description: 'Demo site tag title'
  },
  docsTag: {
    id: 'webchat.resourcesTab.docsTag',
    defaultMessage: 'Docs',
    description: 'Docs tag title'
  },
  tutorialTag: {
    id: 'webchat.resourcesTab.tutorialTag',
    defaultMessage: 'Tutorial',
    description: 'Tutorial tag title'
  },
  docsTutorialTitle: {
    id: 'webchat.resourcesTab.docsTutorialTitle',
    defaultMessage: 'Docs & Tutorials',
    description: 'Docs & Tutorials title'
  },
  demoSiteTileTitle: {
    id: 'webchat.resourcesTab.demoSiteTileTitle',
    defaultMessage: 'I’m new to web chat & want to explore features',
    description: 'Demo site tile title'
  },
  docsTutorialTileTitle: {
    id: 'webchat.resourcesTab.docsTutorialTileTitle',
    defaultMessage:
      'I want to get additional help & discover more web chat capabilities',
    description: 'Docs Tutorial tile title'
  },
  footer: {
    id: 'webchat.resourcesTab.footer',
    defaultMessage:
      'Still can’t find what you are looking for? Visit the {demoSiteLink} or {docsTutorialLink}',
    description: 'Demo site tag title'
  }
});

export const Validation = defineMessages({
  botNameMaxLength: {
    id: 'webchat.Validation.botNameMaxLength',
    defaultMessage: 'Public assistant name has a max length of 40 characters.', // TODO: 40 should be a variable I pass in I'm assuming...
    description: ''
  },
  botNameRequired: {
    id: 'webchat.Validation.botNameRequired',
    defaultMessage: 'Public assistant name is required.',
    description: ''
  },
  colorPicker: {
    id: 'webchat.Validation.colorPicker',
    defaultMessage: 'Please enter a proper HEX code based color value.',
    description: ''
  },
  contactLabelMaxLength: {
    id: 'webchat.Validation.contactLabelMaxLength',
    defaultMessage: 'Option label has a max length of 100 characters.', // TODO: 100 should be a variable I pass in I'm assuming...
    description: ''
  },
  contactLabelRequired: {
    id: 'webchat.Validation.contactLabelRequired',
    defaultMessage: 'Option label is required.',
    description: ''
  },
  desktopLauncherGreetingRequired: {
    id: 'webchat.Validation.desktopLauncherGreetingRequired',
    defaultMessage: 'Greeting message is required for desktop launcher.',
    description: ''
  },
  greetingMaxLength: {
    id: 'webchat.Validation.greetingMaxLength',
    defaultMessage: 'Greeting has a max length of 90 characters.', // TODO: 90 should be a variable I pass in I'm assuming...
    description: ''
  },
  greetingRequired: {
    id: 'webchat.Validation.greetingRequired',
    defaultMessage: 'Greeting is required.',
    description: ''
  },
  launcherDesktopTitleMaxLength: {
    id: 'webchat.Validation.launcherDesktopTitleMaxLength',
    defaultMessage: `Desktop and tablet proactive message has a max length of ${LAUNCHER_DESKTOP_TITLE_MAX_LENGTH} characters.`,
    description: ''
  },
  launcherMobileTitleMaxLength: {
    id: 'webchat.Validation.launcherMobileTitleMaxLength',
    defaultMessage: `Smart phone proactive message has a max length of ${LAUNCHER_MOBILE_TITLE_MAX_LENGTH} characters.`,
    description: ''
  },
  mobileLauncherGreetingRequired: {
    id: 'webchat.Validation.mobileLauncherGreetingRequired',
    defaultMessage: 'Greeting message is required for mobile launcher.',
    description: ''
  },
  sizePixelValue: {
    id: 'webchat.Validation.sizePixelValue',
    defaultMessage: `Please enter a proper pixel value up to ${WEB_CHAT_SIZE_MAX_LENGTH}.`,
    description: ''
  },
  publicKeyRequired: {
    id: 'webchat.Validation.publicKeyRequired',
    defaultMessage: 'Public key is required.'
  },
  startersMaxLength: {
    id: 'webchat.Validation.startersMaxLength',
    defaultMessage: `Conversation starters have a max length of ${STARTERS_MAX_LENGTH}.`,
    description: ''
  },
  startersRequired: {
    id: 'webchat.Validation.startersRequired',
    defaultMessage: 'You cannot have an empty starter field.',
    description: ''
  }
});

export const WebChat = defineMessages({
  defaultBotName: {
    id: 'webchat.defaultBotName',
    defaultMessage: 'Watson Assistant',
    description: ''
  },
  defaultIntegrationName: {
    id: 'webchat.defaultIntegrationName',
    defaultMessage: 'Web chat',
    description: ''
  },
  embedTabLabel: {
    id: 'webchat.embedTabLabel',
    defaultMessage: 'Embed'
  },
  homeScreenTabLabel: {
    id: 'webchat.homeScreenTabLabel',
    defaultMessage: 'Home screen'
  },
  launcherTabLabel: {
    id: 'webchat.launcherTabLabel',
    defaultMessage: 'Launcher'
  },
  liveAgentTabLabel: {
    id: 'webchat.liveAgentTabLabel',
    defaultMessage: 'Live agent'
  },
  namespaceLabel: {
    id: 'webchat.namespaceLabel',
    defaultMessage: 'Web chat preview',
    description: ''
  },
  securityTabLabel: {
    id: 'webchat.securityTabLabel',
    defaultMessage: 'Security'
  },
  styleTabLabel: {
    id: 'webchat.styleTabLabel',
    defaultMessage: 'Style'
  },
  suggestionsTabLabel: {
    id: 'webchat.suggestionsTabLabel',
    defaultMessage: 'Suggestions'
  },
  resourcesTabLabel: {
    id: 'webchat.resourcesTabLabel',
    defaultMessage: 'Resources'
  }
});

export const WebChatNew = defineMessages({
  customizationDescription: {
    id: 'webchat.WebChatNew.customizationDescription',
    defaultMessage:
      'Additional customization and advanced integration are available for developers when adding web chat to your website.',
    description: ''
  },
  embedDescription: {
    id: 'webchat.WebChatNew.embedDescription',
    defaultMessage:
      'Embed the chat UI into any website pages where you want customers to get help from your assistant.',
    description: ''
  },
  supportedServiceDesksLabel: {
    id: 'webchat.WebChatNew.supportedServiceDesksLabel',
    defaultMessage: 'Supported live agent integrations',
    description: ''
  },
  title: {
    id: 'webchat.WebChatNew.title',
    defaultMessage: 'Embed the assistant in your company website',
    description: ''
  },
  transferDescription: {
    id: 'webchat.WebChatNew.transferDescription',
    defaultMessage:
      'You can also connect the web chat to a service desk solution so your assistant can transfer users to live agents.',
    description: ''
  }
});
